import { useState, FormEvent } from 'react';
import PasswordInput from '../PasswordInput';
import { useLanguage } from '../../i18n/LanguageContext';
import { api } from '../../services/api';
import { WrongAppError, WrongAppTarget } from '../../hooks/useAuth';
import { otherLoginHref } from '../../utils/portalHost';

interface SignInFormProps {
  /** First arg is the username (login identifier after Spec 039, and for dealer users after Spec 085 BR-2). */
  onSubmit: (username: string, password: string, rememberMe: boolean) => Promise<void>;
  /** Partner self-signup. Dealer accounts are created by Garagat only (Spec 085 BR-1), so the dealer login omits it. */
  onCreateAccount?: () => void;
  /** Spec 085 (G16, BR-2): the dealer portal login reuses this form, signing in by username like partners. */
  variant?: 'partner' | 'dealer';
}

export default function SignInForm({ onSubmit, onCreateAccount, variant = 'partner' }: SignInFormProps) {
  const { t } = useLanguage();
  const isDealer = variant === 'dealer';
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [wrongApp, setWrongApp] = useState<WrongAppTarget | null>(null);
  const [loading, setLoading] = useState(false);

  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetUsername, setResetUsername] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);

  // Forgot username state
  const [showForgotUsername, setShowForgotUsername] = useState(false);
  const [recoverEmail, setRecoverEmail] = useState('');
  const [recoverPhone, setRecoverPhone] = useState('');
  const [recoverLoading, setRecoverLoading] = useState(false);
  const [recoverSent, setRecoverSent] = useState(false);
  const [recoverError, setRecoverError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setWrongApp(null);

    if (!username.trim()) {
      setError(t('auth.username.required'));
      return;
    }
    if (!password) {
      setError(t('auth.password.required'));
      return;
    }

    setLoading(true);
    try {
      await onSubmit(username.trim().toLowerCase(), password, rememberMe);
    } catch (err) {
      if (err instanceof WrongAppError) {
        setWrongApp(err.target);
      } else {
        setError(err instanceof Error ? err.message : t('auth.loginFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: FormEvent) => {
    e.preventDefault();
    if (!resetUsername.trim()) {
      setResetError(t('auth.resetUsername.required'));
      return;
    }
    setResetLoading(true);
    setResetError(null);
    try {
      // By username for partners and dealers alike: an email can be shared by several accounts
      // (a dealer user's email may also be on their customer account), a username names exactly one.
      await api.requestPasswordReset({ username: resetUsername.trim().toLowerCase() });
      setResetSent(true);
    } catch {
      setResetError(t('auth.resetUsername.failed'));
    } finally {
      setResetLoading(false);
    }
  };

  const closeForgotPassword = () => {
    setShowForgotPassword(false);
    setResetUsername('');
    setResetSent(false);
    setResetError(null);
  };

  const handleForgotUsername = async (e: FormEvent) => {
    e.preventDefault();
    if (!recoverEmail.trim() && !recoverPhone.trim()) {
      setRecoverError(t('auth.recoverUsername.required'));
      return;
    }
    setRecoverLoading(true);
    setRecoverError(null);
    try {
      await api.recoverUsername({
        email: recoverEmail.trim() || undefined,
        phone: recoverPhone.trim() ? `971${recoverPhone.trim()}` : undefined,
      });
      setRecoverSent(true);
    } catch {
      setRecoverError(t('auth.recoverUsername.failed'));
    } finally {
      setRecoverLoading(false);
    }
  };

  const closeForgotUsername = () => {
    setShowForgotUsername(false);
    setRecoverEmail('');
    setRecoverPhone('');
    setRecoverSent(false);
    setRecoverError(null);
  };

  return (
    <>
      <div className="auth-brand">
        <h1>{t('auth.brand')}</h1>
        {isDealer && <p className="dealer-login-brandline">{t('dealer.login.brandLine')}</p>}
      </div>

      <h2 className="auth-title">{t(isDealer ? 'dealer.login.signIn' : 'auth.signIn')}</h2>
      <p className="auth-subtitle">{t('auth.signIn.subtitle')}</p>

      {error && <div className="error-message">{error}</div>}
      {wrongApp && (
        <div className="error-message" role="alert">
          {t(wrongApp === 'dealer' ? 'dealer.wrongApp.dealerAccount' : 'dealer.wrongApp.notDealerAccount')}{' '}
          <a href={otherLoginHref(wrongApp)}>
            {t(wrongApp === 'dealer' ? 'dealer.wrongApp.goDealer' : 'dealer.wrongApp.goPartner')}
          </a>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="username">{t('auth.username')}</label>
          <div className="field-prefix-group">
            <span className="field-prefix-icon" aria-hidden="true">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 21v-2a4 4 0 0 1 4-4h8a4 4 0 0 1 4 4v2"/>
                <circle cx="12" cy="7" r="4"/>
              </svg>
            </span>
            <input
              type="text"
              id="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder={t('auth.username.placeholder')}
              required
              disabled={loading}
              autoComplete="username"
              dir="ltr"
              spellCheck={false}
            />
          </div>
          {!isDealer && <div className="auth-tip">{t('auth.username.tipLegacy')}</div>}
        </div>

        <div className="form-group">
          <label htmlFor="password">{t('auth.password')}</label>
          <div className="field-prefix-group">
            <span className="field-prefix-icon" aria-hidden="true">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              </svg>
            </span>
            <PasswordInput
              id="password"
              value={password}
              onChange={setPassword}
              placeholder={t('auth.password.placeholder')}
              disabled={loading}
              required
            />
          </div>
        </div>

        <div className="form-row">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
            />
            <span>{t('auth.rememberMe')}</span>
          </label>
          <div className="forgot-links">
            <a href="#" className="forgot-link" onClick={(e) => { e.preventDefault(); setShowForgotPassword(true); }}>
              {t('auth.forgotPassword')}
            </a>
            {!isDealer && (
              <a href="#" className="forgot-link" onClick={(e) => { e.preventDefault(); setShowForgotUsername(true); }}>
                {t('auth.forgotUsername')}
              </a>
            )}
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? t('auth.signingIn') : t(isDealer ? 'dealer.login.signIn' : 'auth.signIn')}
          </button>
        </div>
        {isDealer && <p className="dealer-login-invite">{t('dealer.login.inviteHint')}</p>}
      </form>

      {onCreateAccount ? (
        <div className="form-footer">
          <p>
            {t('auth.noAccount')}{' '}
            <a href="#" onClick={(e) => { e.preventDefault(); onCreateAccount(); }}>
              {t('auth.createAccount')}
            </a>
          </p>
        </div>
      ) : isDealer && (
        <div className="form-footer">
          <p>{t('dealer.login.noSelfSignup')} <a href="/login">{t('dealer.login.partnerLink')}</a></p>
        </div>
      )}

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 420 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600 }}>{t('auth.resetPassword')}</h3>
              <button onClick={closeForgotPassword} style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer', color: '#6b7280' }}>&times;</button>
            </div>

            {resetSent ? (
              <div>
                <div style={{ textAlign: 'center', padding: '1rem 0' }}>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: '0.75rem' }}>
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                    <polyline points="22 4 12 14.01 9 11.01"/>
                  </svg>
                  <p style={{ fontWeight: 500, marginBottom: '0.5rem' }}>{t('auth.checkEmail')}</p>
                  <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                    {t('auth.resetUsername.success')}
                  </p>
                </div>
                <button className="btn btn-primary" onClick={closeForgotPassword} style={{ width: '100%', marginTop: '1rem' }}>
                  {t('common.backToSignIn')}
                </button>
              </div>
            ) : (
              <form onSubmit={handleForgotPassword}>
                <p style={{ color: '#6b7280', fontSize: '0.875rem', marginBottom: '1rem' }}>
                  {t('auth.resetUsername.instruction')}
                </p>
                {resetError && <div className="error-message" style={{ marginBottom: '0.75rem' }}>{resetError}</div>}
                <div className="form-group">
                  <label htmlFor="reset-username">{t('auth.resetUsername.label')}</label>
                  <input
                    type="text"
                    id="reset-username"
                    value={resetUsername}
                    onChange={(e) => setResetUsername(e.target.value)}
                    placeholder={t('auth.resetUsername.placeholder')}
                    required
                    disabled={resetLoading}
                    autoFocus
                    dir="ltr"
                    spellCheck={false}
                    style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '0.875rem' }}
                  />
                </div>
                <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
                  <button type="button" className="btn btn-secondary" onClick={closeForgotPassword} style={{ flex: 1 }}>
                    {t('common.cancel')}
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={resetLoading} style={{ flex: 1 }}>
                    {resetLoading ? t('auth.resetUsername.sending') : t('auth.resetUsername.send')}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Forgot Username Modal */}
      {showForgotUsername && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 420 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600 }}>{t('auth.recoverUsername')}</h3>
              <button onClick={closeForgotUsername} style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer', color: '#6b7280' }}>&times;</button>
            </div>

            {recoverSent ? (
              <div>
                <div style={{ textAlign: 'center', padding: '1rem 0' }}>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: '0.75rem' }}>
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                    <polyline points="22 4 12 14.01 9 11.01"/>
                  </svg>
                  <p style={{ fontWeight: 500, marginBottom: '0.5rem' }}>{t('auth.checkEmail')}</p>
                  <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>{t('auth.recoverUsername.success')}</p>
                </div>
                <button className="btn btn-primary" onClick={closeForgotUsername} style={{ width: '100%', marginTop: '1rem' }}>
                  {t('common.backToSignIn')}
                </button>
              </div>
            ) : (
              <form onSubmit={handleForgotUsername}>
                <p style={{ color: '#6b7280', fontSize: '0.875rem', marginBottom: '1rem' }}>
                  {t('auth.recoverUsername.instruction')}
                </p>
                {recoverError && <div className="error-message" style={{ marginBottom: '0.75rem' }}>{recoverError}</div>}
                <div className="form-group">
                  <label htmlFor="recover-email">{t('auth.email')}</label>
                  <input
                    type="email"
                    id="recover-email"
                    value={recoverEmail}
                    onChange={(e) => setRecoverEmail(e.target.value)}
                    placeholder={t('auth.email.placeholder')}
                    disabled={recoverLoading}
                    autoFocus
                    style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '0.875rem' }}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="recover-phone">{t('auth.phone')}</label>
                  <div className="phone-input-group">
                    <span className="phone-prefix">971</span>
                    <input
                      type="tel"
                      id="recover-phone"
                      value={recoverPhone}
                      onChange={(e) => setRecoverPhone(e.target.value)}
                      placeholder={t('auth.phone.placeholder')}
                      disabled={recoverLoading}
                    />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
                  <button type="button" className="btn btn-secondary" onClick={closeForgotUsername} style={{ flex: 1 }}>
                    {t('common.cancel')}
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={recoverLoading} style={{ flex: 1 }}>
                    {recoverLoading ? t('auth.recoverUsername.sending') : t('auth.recoverUsername.send')}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
